import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthContext";

import TopNav from "../components/navigation/TopNav";
import StandardScreen, {
  ScreenContent,
} from "../components/layout/StandardScreen";
import Stack from "../components/layout/Stack";
import Inline from "../components/layout/Inline";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import FormField from "../components/forms/FormField";
import TextInput from "../components/forms/TextInput";
import SelectInput from "../components/forms/SelectInput";
import TextArea from "../components/forms/TextArea";

const errorTextStyle = {
  color: "var(--color-danger)",
};

const disabledInputStyle = {
  width: "100%",
  minHeight: "40px",
  padding: "0 12px",
  border: "1px solid var(--color-border, #d0d7de)",
  borderRadius: "8px",
  backgroundColor: "var(--color-surface-muted, #f6f8fa)",
  color: "var(--color-text, inherit)",
  fontSize: "var(--font-size-md)",
  opacity: 1,
};

const ManageExcursion = () => {
  const { t } = useTranslation();
  const { user, loadingUser } = useAuth();

  const [excursions, setExcursions] = useState([]);
  const [selectedExcursionId, setSelectedExcursionId] = useState("");
  const [selectedExcursion, setSelectedExcursion] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingExcursions, setIsLoadingExcursions] = useState(false);
  const [isLoadingSelected, setIsLoadingSelected] = useState(false);
  const [pageError, setPageError] = useState("");

  const token = localStorage.getItem("token");

  const headers = useMemo(() => {
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const eligible = user?.role === "admin";

  useEffect(() => {
    const fetchExcursions = async () => {
      if (!eligible) return;

      try {
        setIsLoadingExcursions(true);
        setPageError("");

        const exRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/excursions/i-can-see`,
          { headers }
        );

        const data = Array.isArray(exRes.data) ? exRes.data : [];
        setExcursions(data);

        if (user?.excursion?.excursion_id) {
          const activeId = String(user.excursion.excursion_id);
          const existsInList = data.some(
            (ex) => String(ex.excursion_id) === activeId
          );

          if (existsInList) {
            setSelectedExcursionId(activeId);
          }
        }
      } catch (err) {
        console.error(err);
        setPageError(t("manage_excursion.errors.load_excursions"));
      } finally {
        setIsLoadingExcursions(false);
      }
    };

    if (user && token) {
      fetchExcursions();
    }
  }, [user, eligible, headers, token, t]);

  useEffect(() => {
    const fetchSelectedExcursion = async () => {
      if (!selectedExcursionId || !eligible) {
        setSelectedExcursion(null);
        return;
      }

      try {
        setIsLoadingSelected(true);
        setPageError("");

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/excursions/${selectedExcursionId}/b`,
          { headers }
        );

        setSelectedExcursion(res.data || null);
      } catch (err) {
        console.error(err);
        setPageError(t("manage_excursion.errors.load_selected"));
      } finally {
        setIsLoadingSelected(false);
      }
    };

    fetchSelectedExcursion();
  }, [selectedExcursionId, eligible, headers, t]);

  const handleChange = (field, value) => {
    if (!selectedExcursion) return;

    setSelectedExcursion((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdate = async () => {
    if (!selectedExcursion || isSaving) return;

    const payload = {
      short_name: selectedExcursion.short_name,
      definition: selectedExcursion.definition,
      start_date: selectedExcursion.start_date,
      end_date: selectedExcursion.end_date,
      excursion_currency: selectedExcursion.excursion_currency,
      status: selectedExcursion.status,
    };

    try {
      setIsSaving(true);

      await axios.put(
        `${process.env.REACT_APP_API_URL}/excursions/${selectedExcursion.excursion_id}/update`,
        payload,
        { headers }
      );

      alert(t("manage_excursion.alerts.update_success"));
    } catch (err) {
      console.error(err);
      alert(t("manage_excursion.alerts.update_failed"));
    } finally {
      setIsSaving(false);
    }
  };

  if (loadingUser) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("manage_excursion.title")} mode="main" />}
        isLoading
      />
    );
  }

  if (!user) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("manage_excursion.title")} mode="main" />}
        isError
        errorState={
          <div style={errorTextStyle}>
            {t("manage_excursion.errors.no_user")}
          </div>
        }
      />
    );
  }

  if (!eligible) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("manage_excursion.title")} mode="main" />}
      >
        <ScreenContent>
          <Card variant="accent">
            <strong>{t("manage_excursion.errors.no_access")}</strong>
          </Card>
        </ScreenContent>
      </StandardScreen>
    );
  }

  const selectedExcursionData = excursions.find(
    (ex) => String(ex.excursion_id) === String(selectedExcursionId)
  );

  const selectedExcursionLabel = selectedExcursionData
    ? `${selectedExcursionData.short_name} (${selectedExcursionData.start_date} → ${selectedExcursionData.end_date})`
    : isLoadingExcursions
    ? t("manage_excursion.loading.excursions")
    : t("manage_excursion.no_selection");

  return (
    <StandardScreen
      topNav={<TopNav title={t("manage_excursion.title")} mode="main" />}
      bottomAction={
        <Button
          fullWidth
          size="lg"
          onClick={handleUpdate}
          disabled={!selectedExcursion || isSaving || isLoadingSelected}
        >
          {isSaving
            ? t("manage_excursion.actions.updating")
            : t("manage_excursion.actions.update")}
        </Button>
      }
    >
      <ScreenContent>
        <Stack gap="5">
          <Card title={t("manage_excursion.my_excursion")}>
            <FormField>
              <input
                type="text"
                value={selectedExcursionLabel}
                disabled
                readOnly
                style={disabledInputStyle}
              />
            </FormField>

            {pageError ? (
              <div style={errorTextStyle}>{pageError}</div>
            ) : null}
          </Card>

          {isLoadingSelected && (
            <Card title={t("manage_excursion.details.title")}>
              {t("manage_excursion.loading.details")}
            </Card>
          )}

          {selectedExcursion && (
            <Card title={t("manage_excursion.details.title")}>
              <FormField label={t("manage_excursion.fields.short_name")}>
                <TextInput
                  value={selectedExcursion.short_name || ""}
                  onChange={(e) => handleChange("short_name", e.target.value)}
                />
              </FormField>

              <FormField label={t("manage_excursion.fields.definition")}>
                <TextArea
                  value={selectedExcursion.definition || ""}
                  onChange={(e) => handleChange("definition", e.target.value)}
                />
              </FormField>

              <FormField label={t("manage_excursion.fields.start_date")}>
                <TextInput
                  type="date"
                  value={selectedExcursion.start_date || ""}
                  onChange={(e) => handleChange("start_date", e.target.value)}
                />
              </FormField>

              <FormField label={t("manage_excursion.fields.end_date")}>
                <TextInput
                  type="date"
                  value={selectedExcursion.end_date || ""}
                  onChange={(e) => handleChange("end_date", e.target.value)}
                />
              </FormField>

              <FormField label={t("manage_excursion.fields.currency")}>
                <SelectInput
                  value={selectedExcursion.excursion_currency || ""}
                  onChange={(e) =>
                    handleChange("excursion_currency", e.target.value)
                  }
                >
                  <option value="TRL">TRL</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                </SelectInput>
              </FormField>

              <FormField label={t("manage_excursion.fields.status")}>
                <SelectInput
                  value={selectedExcursion.status || ""}
                  onChange={(e) => handleChange("status", e.target.value)}
                >
                  <option value="locked">locked</option>
                  <option value="live">live</option>
                </SelectInput>
              </FormField>
            </Card>
          )}
        </Stack>
      </ScreenContent>
    </StandardScreen>
  );
};

export default ManageExcursion;